/* @flow */
import { isAdmin } from 'shapes/Affiliation';
import Checkbox from 'components/Checkbox';
import cx from 'classnames';
import ConfirmDialog from 'components/ConfirmDialog';
import { FormattedMessage } from 'react-intl';
import GroupAvatar from 'groups/components/GroupAvatar';
import GroupName from 'groups/components/GroupName';
import HumanArray from 'components/HumanArray';
import * as React from 'react';
import OrganizationActions from 'actions/OrganizationActions';
import ToastActions from 'actions/ToastActions';

import './styles/SettingsRemoveSchoolModal.scss';

import type { Affiliation } from 'shapes/Affiliation';

type RemoveActionProps = {
  checked: boolean,
  onCheck: Function,
  className?: string,
  style?: Object,
  children?: React.Node,
};

const RemoveAction = (props: RemoveActionProps) => {
  return (
    <div
      className={cx('SettingsRemoveSchoolModal-removeContainer', props.className)}
      style={props.style}>
      <Checkbox
        type="checkbox"
        checked={props.checked}
        onChange={props.onCheck}
        className="SettingsRemoveSchoolModal-removeContainer-checkbox"
      />
      <div>{props.children}</div>
    </div>
  );
};

export type Props = {
  schoolId: number,
  schoolName: string,
  affiliation?: ?Affiliation,
  ownedGroups: *[],
  subscribedGroups: *[],
  onConfirm?: Function,
};

type State = {
  subscribedGroupsChecked: { [Backbone$Id]: boolean },
  needsAdmin: boolean,
  needsOwnedGroups: boolean,
};

class SettingsRemoveSchoolModal extends React.Component<Props, State> {
  static defaultProps = {
    onConfirm: () => {},
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      ...this.getNeeds(props),
      subscribedGroupsChecked: {},
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    this.setState({ ...this.state, ...this.getNeeds(nextProps, this.props) });
  }

  getNeeds = (props: Props, prevProps?: Props) => {
    // We don't want a change in props to remove your need to have done something
    return {
      needsAdmin: (prevProps && this.isAdmin(prevProps)) || this.isAdmin(props),
      needsOwnedGroups: (prevProps && this.isGroupOwner(prevProps)) || this.isGroupOwner(props),
    };
  };

  preConfirmPromises = () => {
    const { schoolId, schoolName } = this.props;
    return OrganizationActions.leaveOrg(schoolId, true).then(() => {
      ToastActions.showSuccessToast(`You left ${schoolName}`);
    });
  };

  isAdmin = (props: Props) => {
    return !!props.affiliation && isAdmin(props.affiliation);
  };

  isGroupOwner = (props: Props) => {
    return !!props.ownedGroups && !!props.ownedGroups.length;
  };

  isGroupSubscriber = (props: Props) => {
    return !!props.subscribedGroups && !!props.subscribedGroups.length;
  };

  isUnblocked = (props: Props) => {
    return !this.isAdmin(props) && !this.isGroupSubscriber(props) && !this.isGroupOwner(props);
  };

  renderAdmin = () => {
    return (
      <RemoveAction
        className="RemoveAction-admin"
        onCheck={() => this.setState({ needsAdmin: !this.state.needsAdmin })}
        checked={!this.state.needsAdmin}>
        <div className="SettingsRemoveSchoolModal-actionLabel">
          Remove yourself as administrator.
        </div>
        <div className="SettingsRemoveSchoolModal-actionText">
          Once you do, you won’t have access to your school’s staff list, staff messaging, and other
          features for verified administrators.
        </div>
      </RemoveAction>
    );
  };

  renderOwnedGroups = () => {
    return (
      <RemoveAction
        className="RemoveAction-unlinkGroups"
        onCheck={() =>
          this.setState({
            needsOwnedGroups: !this.state.needsOwnedGroups,
          })
        }
        checked={!this.state.needsOwnedGroups}>
        <div className="SettingsRemoveSchoolModal-actionLabel">Disconnect your classes.</div>
        <div className={cx('SettingsRemoveSchoolModal-actionText', 'description')}>
          <FormattedMessage
            className="description"
            id="settings.removeSchool"
            defaultMessage="The {ownedGroupsCount, plural, one {# class} other {# classes}} you own won’t appear when members of {schoolName} search for classes. They’ll be labeled “not affiliated” unless you join a new school."
            values={{
              ownedGroupsCount: this.props.ownedGroups.length,
              schoolName: this.props.schoolName,
            }}
          />
        </div>
      </RemoveAction>
    );
  };

  renderSubscribedGroups = () => {
    const onGroupCheck = groupId => {
      const subscribedGroupsChecked = {
        ...this.state.subscribedGroupsChecked,
        [groupId]: !this.state.subscribedGroupsChecked[groupId],
      };
      this.setState({
        subscribedGroupsChecked,
      });
    };
    return (
      <div>
        <div className="SettingsRemoveSchoolModal-subscriptionHeader">
          <div className="SettingsRemoveSchoolModal-actionLabel">
            {`Leave classes at ${this.props.schoolName}`}
          </div>
          <div className={cx('SettingsRemoveSchoolModal-actionText', 'description')}>
            Classes affiliated with a school can only include participants from that school.
          </div>
        </div>
        {this.props.subscribedGroups.map(group => {
          const ownerNames = group.owners ? group.owners.map(o => o.user.name) : [];
          return (
            <RemoveAction
              key={group.id}
              style={{
                alignItems: 'center',
                margin: '12px 0',
                paddingBottom: '12px',
              }}
              className={`RemoveAction-leave-${group.id}`}
              onCheck={() => onGroupCheck(group.id)}
              checked={this.state.subscribedGroupsChecked[group.id] || false}>
              <div className="SettingsRemoveSchoolModal-subscription">
                <GroupAvatar group={group} size="small" />
                <div className="SettingsRemoveSchoolModal-subscription-names">
                  <GroupName group={group} />
                  <div className="SettingsRemoveSchoolModal-subscription-names-leaveNames">
                    <HumanArray sample={ownerNames} total={ownerNames.length} />
                  </div>
                </div>
              </div>
            </RemoveAction>
          );
        })}
      </div>
    );
  };

  renderTitle = () => {
    return <span>{`Leave ${this.props.schoolName}?`}</span>;
  };

  renderSubtext = () => {
    if (this.isUnblocked(this.props)) {
      return <span>{`Once you do, you won’t be affiliated with ${this.props.schoolName}.`}</span>;
    } else {
      return <span>{'To leave this school, take a minute to complete this checklist'}</span>;
    }
  };

  render() {
    const { needsAdmin, needsOwnedGroups, subscribedGroupsChecked } = this.state;
    const needsSubscribedGroups = this.props.subscribedGroups.some(
      g => !subscribedGroupsChecked[g.id]
    );
    return (
      <ConfirmDialog
        className="SettingsRemoveSchoolModal"
        eventScreenName="SettingsRemoveSchoolModal"
        {...this.props}
        size="small"
        title={this.renderTitle()}
        subtext={this.renderSubtext()}
        hideCancel
        actionButtonText="Leave"
        preConfirmPromise={this.preConfirmPromises}
        divided
        onConfirm={this.props.onConfirm}
        confirmDisabled={needsAdmin || needsOwnedGroups || needsSubscribedGroups}>
        {this.isAdmin(this.props) && this.renderAdmin()}
        {this.isGroupOwner(this.props) && this.renderOwnedGroups()}
        {this.isGroupSubscriber(this.props) && this.renderSubscribedGroups()}
      </ConfirmDialog>
    );
  }
}

export default SettingsRemoveSchoolModal;
